import { randomBytes, createHash } from 'crypto';
import type { OAuthAuthorizationParams } from '../types';

/**
 * PKCE (Proof Key for Code Exchange) utility functions
 * Implements RFC 7636 for secure OAuth 2.1 flows
 */
export class PKCEUtils {
	/**
	 * Generate PKCE parameters for OAuth authorization
	 * 
	 * @returns Object containing code verifier, challenge, and state
	 * 
	 * @example
	 * ```typescript
	 * const pkceParams = PKCEUtils.generateParams();
	 * const authUrl = client.getAuthorizationUrl(pkceParams);
	 * ```
	 */
	static generateParams(): OAuthAuthorizationParams {
		const codeVerifier = randomBytes(32).toString('base64url');
		const codeChallenge = createHash('sha256')
			.update(codeVerifier)
			.digest('base64url');
		const state = randomBytes(16).toString('hex');

		return {
			codeVerifier,
			codeChallenge,
			state,
		};
	}

	/**
	 * Validate code verifier format
	 * 
	 * @param codeVerifier - The code verifier to validate
	 * @returns True if valid, false otherwise
	 */
	static isValidCodeVerifier(codeVerifier: string): boolean {
		// RFC 7636: code_verifier = 43*128unreserved
		// unreserved = ALPHA / DIGIT / "-" / "." / "_" / "~"
		const validPattern = /^[A-Za-z0-9\-._~]{43,128}$/;
		return validPattern.test(codeVerifier);
	}

	/**
	 * Generate code challenge from verifier
	 * 
	 * @param codeVerifier - The code verifier
	 * @returns The SHA256 base64url encoded challenge
	 */
	static generateChallenge(codeVerifier: string): string {
		if (!this.isValidCodeVerifier(codeVerifier)) {
			throw new Error('Invalid code verifier format');
		}

		return createHash('sha256')
			.update(codeVerifier)
			.digest('base64url');
	}
}
